/**
 * Calculates velocity using decoupled position and time stacks.
 * Position deltas are collected at pointer event rate (irregular).
 * Time deltas are collected at rAF rate (consistent ~16.67ms).
 * Velocity is calculated on-demand by dividing sum of positions by sum of times.
 */
export default class VelocityCalculator {
	/** @type {number[]} - Last N position deltas from pointer events */
	#posDeltas = [];

	/** @type {number[]} - Last N time deltas from rAF ticks */
	#timeDeltas = [];

	/** @type {number} - Number of samples to keep in each stack */
	#historySize = 4;

	/** @type {number} - Timestamp of previous rAF tick */
	#prevTime = 0;

	/** @type {number|null} - rAF ID for cancellation */
	#rafId = null;

	/** @type {boolean} - Whether the calculator is actively running */
	#isRunning = false;

	/** @type {number} - Reference frame time for normalization (60fps = 16.67ms) */
	#referenceTime = 16.67;

	/**
	 * Start tracking velocity
	 */
	start() {
		const _ = this;
		_.stop();
		_.#posDeltas = [];
		_.#timeDeltas = [];
		_.#prevTime = performance.now();
		_.#isRunning = true;
		_.#tick();
	}

	/**
	 * Add a position delta from a pointer event
	 * @param {number} delta - Position change since last pointer event
	 */
	addDelta(delta) {
		this.#posDeltas.push(delta);
		if (this.#posDeltas.length > this.#historySize) {
			this.#posDeltas.shift();
		}
	}

	/**
	 * Stop tracking and return the final velocity
	 * @returns {number} - Final velocity normalized to 60fps baseline
	 */
	stop() {
		const _ = this;
		_.#isRunning = false;
		if (_.#rafId !== null) {
			cancelAnimationFrame(_.#rafId);
			_.#rafId = null;
		}
		return _.getVelocity();
	}

	/**
	 * Get the current velocity without stopping
	 * Calculates on-demand from position and time stacks
	 * @returns {number} - Current velocity normalized to 60fps baseline
	 */
	getVelocity() {
		const _ = this;
		if (_.#posDeltas.length === 0 || _.#timeDeltas.length === 0) {
			return 0;
		}

		const totalPos = _.#posDeltas.reduce((a, b) => a + b, 0);
		const totalTime = _.#timeDeltas.reduce((a, b) => a + b, 0);

		if (totalTime === 0) return 0;

		// Calculate velocity and clamp to reasonable bounds to prevent extreme values
		const velocity = (totalPos / totalTime) * _.#referenceTime;
		return Math.max(-5000, Math.min(5000, velocity));
	}

	/**
	 * Internal rAF tick that collects time deltas
	 * @param {number} [time] - rAF timestamp
	 */
	#tick(time) {
		const _ = this;
		if (!_.#isRunning) return;

		if (time !== undefined) {
			const deltaTime = time - _.#prevTime;

			if (deltaTime > 0) {
				_.#timeDeltas.push(deltaTime);
				if (_.#timeDeltas.length > _.#historySize) {
					_.#timeDeltas.shift();
				}
			}

			_.#prevTime = time;
		}

		_.#rafId = requestAnimationFrame((t) => _.#tick(t));
	}
}
